import { DEFAULT_DASHBOARD_CONFIG, OVERVIEW_CARD_IDS } from '../config/defaultDashboardConfig';
import { DASHBOARD_QUICK_ACTION_CATALOG } from '../config/dashboardQuickActionCatalog';
import { SALES_OVERVIEW_TEMPLATE_IDS } from '../components/dashboard/salesOverview/salesOverviewConstants';

export const getDashboardConfigCacheKey = (companyId) =>
  companyId ? `dashboard_config_${companyId}` : 'dashboard_config';

/** Settings store `pending`; legacy builds used `due` on the overview card object. */
const normalizeOverviewCardIds = (ids) =>
  (ids || []).map((id) => (id === 'due' ? 'pending' : id));

export const mergeDashboardConfig = (saved) => {
  if (!saved || typeof saved !== 'object') {
    return JSON.parse(JSON.stringify(DEFAULT_DASHBOARD_CONFIG));
  }
  const merged = {
    ...DEFAULT_DASHBOARD_CONFIG,
    ...saved,
    overviewCards: {
      ...DEFAULT_DASHBOARD_CONFIG.overviewCards,
      ...(saved.overviewCards || {}),
    },
    quickActions: {
      ...DEFAULT_DASHBOARD_CONFIG.quickActions,
      ...(saved.quickActions || {}),
      slots: mergeQuickActionSlots(saved.quickActions?.slots),
    },
    sections: saved.sections?.length ? saved.sections : DEFAULT_DASHBOARD_CONFIG.sections,
    salesChart: {
      ...DEFAULT_DASHBOARD_CONFIG.salesChart,
      ...(saved.salesChart || {}),
      template: normalizeSalesOverviewTemplate(saved?.salesChart),
    },
    lowStock: {
      ...DEFAULT_DASHBOARD_CONFIG.lowStock,
      ...(saved.lowStock || {}),
    },
    alerts: {
      ...DEFAULT_DASHBOARD_CONFIG.alerts,
      ...(saved.alerts || {}),
    },
  };
  if (merged.overviewCards?.enabled) {
    merged.overviewCards.enabled = normalizeOverviewCardIds(merged.overviewCards.enabled);
  }
  return merged;
};

/** Resolve sales overview template (migrates legacy area/bar/line variant → template1). */
export const normalizeSalesOverviewTemplate = (salesChart) => {
  const template = salesChart?.template;
  if (template && SALES_OVERVIEW_TEMPLATE_IDS.includes(template)) {
    return template;
  }
  return 'template1';
};

export const isSectionVisible = (config, sectionId) => {
  const section = config?.sections?.find((s) => s.id === sectionId);
  return section?.visible !== false;
};

/** MUI Grid column span (of 12) so visible widgets evenly fill the row. */
export const getGridColumnSpan = (visibleCount) => {
  if (!visibleCount || visibleCount < 1) return 12;
  return Math.floor(12 / visibleCount);
};

export const countVisibleSections = (config, sectionIds) =>
  sectionIds.filter((id) => isSectionVisible(config, id)).length;

/** Full list order for settings UI (enabled first in saved order, then disabled). */
export const getOverviewCardDisplayOrder = (config) => {
  const enabled = config?.overviewCards?.enabled || DEFAULT_DASHBOARD_CONFIG.overviewCards.enabled;
  const enabledSet = new Set(enabled);
  const disabled = OVERVIEW_CARD_IDS.filter((id) => !enabledSet.has(id));
  return [...enabled, ...disabled];
};

export const getEnabledOverviewCardIds = (config) => {
  const enabledSet = new Set(
    config?.overviewCards?.enabled || DEFAULT_DASHBOARD_CONFIG.overviewCards.enabled
  );
  const enabledOrdered = getOverviewCardDisplayOrder(config).filter((id) => enabledSet.has(id));
  const configuredMax = config?.overviewCards?.maxVisible ?? 8;
  const cap = Math.min(9, Math.max(configuredMax, enabledOrdered.length));
  return enabledOrdered.slice(0, cap);
};

export const filterOverviewCards = (allCards, config) => {
  const order = getEnabledOverviewCardIds(config);
  const byId = Object.fromEntries(allCards.map((c) => [c.id, c]));
  return order.map((id) => byId[id]).filter(Boolean);
};

/** Merge saved slots with catalog defaults so new actions appear for existing companies. */
export const mergeQuickActionSlots = (savedSlots) => {
  const defaults = DEFAULT_DASHBOARD_CONFIG.quickActions.slots;
  if (!savedSlots?.length) {
    return defaults.map((slot) => ({ ...slot }));
  }
  const byId = Object.fromEntries(savedSlots.map((slot) => [slot.id, slot]));
  const merged = defaults.map((def) => ({ ...def, ...byId[def.id] }));
  savedSlots.forEach((slot) => {
    if (slot?.id && !merged.some((m) => m.id === slot.id)) {
      merged.push({ ...slot });
    }
  });
  return merged;
};

export const hasDuplicateQuickActionKeys = (slots) => {
  const keys = (slots || [])
    .filter((slot) => slot?.key && DASHBOARD_QUICK_ACTION_CATALOG[slot.id])
    .map((slot) => slot.key);
  return keys.length !== new Set(keys).size;
};

/**
 * Assign a shortcut key; if another action already uses it, swap keys with that action.
 * Example: New Sale F1 → pick F2 while Purchase Entry has F2 → New Sale F2, Purchase Entry F1.
 */
export const applyQuickActionKeyChange = (slots, index, newKey) => {
  const next = (slots || []).map((slot) => ({ ...slot }));
  if (!next[index] || !newKey || newKey === next[index].key) {
    return next;
  }
  const previousKey = next[index].key;
  const conflictIndex = next.findIndex((slot, i) => i !== index && slot.key === newKey);
  if (conflictIndex >= 0 && previousKey) {
    next[conflictIndex] = { ...next[conflictIndex], key: previousKey };
  }
  next[index] = { ...next[index], key: newKey };
  return next;
};

export const resolveQuickActions = (config) => {
  if (config?.quickActions?.enabled === false) {
    return [];
  }
  const slots = mergeQuickActionSlots(config?.quickActions?.slots);
  return slots
    .filter((slot) => slot.visible !== false && DASHBOARD_QUICK_ACTION_CATALOG[slot.id])
    .map((slot) => {
      const meta = DASHBOARD_QUICK_ACTION_CATALOG[slot.id];
      return {
        id: slot.id,
        label: meta.label,
        path: meta.path,
        shortcut: slot.key,
        key: slot.key,
      };
    });
};

export const filterLowStockItems = (items, config) => {
  if (config?.lowStock?.includeOutOfStock !== false) {
    return items;
  }
  return (items || []).filter((item) => item.stockStatus !== 'out');
};

export const getDashboardAlertFlags = (config) => ({
  ...DEFAULT_DASHBOARD_CONFIG.alerts,
  ...(config?.alerts || {}),
});

/** Whether a dashboard alert type is enabled in company settings. */
export const isDashboardAlertEnabled = (config, alertId) => {
  const flags = getDashboardAlertFlags(config);
  if (alertId === 'out-of-stock') return flags.outOfStock !== false;
  if (alertId === 'low-stock') return flags.lowStock !== false;
  if (alertId === 'credit-due') return flags.creditDue !== false;
  if (alertId === 'plan-days') return flags.subscription !== false;
  return true;
};

export const filterAlertsByConfig = (alerts, config) =>
  (alerts || []).filter((alert) => isDashboardAlertEnabled(config, alert.id));

export const needsOverviewCard = (config, cardId) =>
  getEnabledOverviewCardIds(config).includes(cardId);

export const canEditDashboardSettings = () => {
  try {
    const role = (localStorage.getItem('userRole') || '').toUpperCase();
    if (role === 'ADMIN' || role === 'MANAGER') return true;
    const stored = localStorage.getItem('user');
    if (stored) {
      const parsed = JSON.parse(stored);
      const r = (parsed.role || '').toUpperCase();
      return r === 'ADMIN' || r === 'MANAGER';
    }
  } catch {
    /* ignore */
  }
  return false;
};
